import { useState, useEffect, useCallback, useRef } from 'react';

import type { ConversationSummary } from '../types';

import { fetchConversationHistory, markConversationRead, markConversationCompleted } from '@/lib/api';

interface UseConversationHistoryParams {
  enabled?: boolean;
  conversationHistoryEndpoint?: string;
  markConversationReadEndpoint?: string;
  markConversationCompletedEndpoint?: string;
  headers?: Record<string, string>;
  userData?: { userId?: string | number };
  currentConversationId: string | undefined;
  loadConversationById: (conversationId: string) => void;
  reconnectHandoff?: (conversationId: string) => void;
  resetHandoff?: () => void;
}

export function useConversationHistory({
  enabled = false,
  conversationHistoryEndpoint,
  markConversationReadEndpoint,
  markConversationCompletedEndpoint,
  headers,
  userData,
  currentConversationId,
  loadConversationById,
  reconnectHandoff,
  resetHandoff,
}: UseConversationHistoryParams) {
  const [showHistory, setShowHistory] = useState(false);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);

  // Tracks the last truly-active conversation before the user browses history,
  const pendingCompletionIdRef = useRef<string | null>(null);

  // Keep headers in a ref so effects always use the latest value without
  const headersRef = useRef(headers);
  headersRef.current = headers;

  const hasUnreadConversations = conversations.some((c) => c.hasUnread);

  const externalUserId = userData?.userId as string | undefined;
  const fingerprint = headers?.['x-bf-fingerprint'];
  const hasUserIdentity = !!(externalUserId || fingerprint);

  // fetch on mount to drive the unread red dot
  useEffect(() => {
    if (!enabled || !conversationHistoryEndpoint || !hasUserIdentity) return;

    let cancelled = false;

    fetchConversationHistory({
      endpoint: conversationHistoryEndpoint,
      headers: headersRef.current,
      externalUserId,
    })
      .then((data) => {
        if (!cancelled) setConversations(data);
      })
      .catch(() => {
        if (!cancelled) setConversations([]);
      });

    return () => {
      cancelled = true;
    };
  }, [conversationHistoryEndpoint, hasUserIdentity, externalUserId, fingerprint]);

  // re-fetch when panel opens
  useEffect(() => {
    if (!enabled || !showHistory || !conversationHistoryEndpoint || !hasUserIdentity) return;

    let cancelled = false;
    setIsLoadingHistory(true);

    fetchConversationHistory({
      endpoint: conversationHistoryEndpoint,
      headers: headersRef.current,
      externalUserId,
    })
      .then((data) => {
        if (!cancelled) setConversations(data);
      })
      .catch(() => {
        if (!cancelled) setConversations([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingHistory(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, showHistory, conversationHistoryEndpoint, hasUserIdentity, externalUserId, fingerprint]);

  const handleSelectConversation = useCallback(
    ({ conversationId, isHandoff, isCompleted }: { conversationId: string; isHandoff: boolean; isCompleted: boolean }) => {
      // Remember the active conversation before switching so completeActiveConversations
      if (currentConversationId && !isViewOnly) {
        pendingCompletionIdRef.current = currentConversationId;
      }

      loadConversationById(conversationId);
      setIsViewOnly(isCompleted);

      if (isHandoff) {
        reconnectHandoff?.(conversationId);
      } else {
        resetHandoff?.();
      }

      if (markConversationReadEndpoint) {
        markConversationRead({
          endpoint: markConversationReadEndpoint,
          conversationId,
          headers: headersRef.current,
        }).catch(() => {});
      }

      setShowHistory(false);
    },
    [enabled, currentConversationId, isViewOnly, loadConversationById, reconnectHandoff, resetHandoff, markConversationReadEndpoint],
  );

  /**
   * Call this at the start of createNewThread.
   * Marks any lingering active conversation(s) as completed and resets view-only state.
   */
  const completeActiveConversations = useCallback(() => {
    if (!markConversationCompletedEndpoint) return;

    const alreadyLoadedActiveIds = conversations.filter((c) => c.status === 'active').map((c) => c.conversationId);

    const idsToComplete = new Set<string>(
      [pendingCompletionIdRef.current, !isViewOnly ? currentConversationId : null, ...alreadyLoadedActiveIds].filter(
        (id): id is string => !!id,
      ),
    );

    // Optimistically update local state so the panel reflects the change immediately
    setConversations((prev) => prev.map((c) => (idsToComplete.has(c.conversationId) ? { ...c, status: 'completed' } : c)));
    idsToComplete.forEach((id) => {
      markConversationCompleted({
        endpoint: markConversationCompletedEndpoint,
        conversationId: id,
        headers: headersRef.current,
      }).catch(() => {});
    });

    pendingCompletionIdRef.current = null;
  }, [markConversationCompletedEndpoint, currentConversationId, isViewOnly, conversations]);

  return {
    showHistory,
    setShowHistory,
    conversations,
    isLoadingHistory,
    hasUnreadConversations,
    isViewOnly,
    setIsViewOnly,
    handleSelectConversation,
    completeActiveConversations,
  };
}
